<template>
  <div class="ExternalProjectsList">
    <div class="header">
      <div class="header-title">External Projects</div>
    </div>
    <div class="panel">
      <div class="ExternalProjectsList__filters">
        <button
          v-for="filter in filters"
          :key="String(filter.value)"
          :class="[
            'ExternalProjectsList__filter-btn',
            {
              'ExternalProjectsList__filter-btn--active':
                activeFilter === filter.value,
            },
          ]"
          @click="setFilter(filter.value)"
        >
          {{ filter.label }}
        </button>
      </div>

      <div v-if="fetchError" class="ExternalProjectsList__error">
        <span>{{ fetchError }}</span>
        <button class="button button-primary" @click="retry">Retry</button>
      </div>

      <table v-else class="ExternalProjectsList__table">
        <thead>
          <tr>
            <th>Project Name</th>
            <th>Owner</th>
            <th>Submitted At</th>
            <th>Status</th>
          </tr>
        </thead>
        <tr
          v-for="project in projects"
          :key="project.id"
          class="ExternalProjectsList__row"
          @click="viewProject(project.id)"
        >
          <td>{{ project.name }}</td>
          <td>
            <a
              :href="`${kcBaseUrl}/user/${project.owner.username}`"
              target="_blank"
              rel="noopener noreferrer"
              class="ExternalProjectsList__owner-link"
              @click.stop
              >{{ project.owner.username }}</a
            >
          </td>
          <td>{{ project.inserted_at | moment('MMM DD, YYYY HH:mm') }}</td>
          <td><status-badge :status="project.status" /></td>
        </tr>
        <tr>
          <td colspan="4">
            <infinite-loading :identifier="infiniteId" @infinite="loadProjects">
              <div slot="no-more"></div>
              <div slot="no-results" class="ExternalProjectsList__empty">
                No projects found.
              </div>
            </infinite-loading>
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import InfiniteLoading from 'vue-infinite-loading';
import StatusBadge from './StatusBadge.vue';
import { REMOTE_PATHS } from '../../constants/remote-paths.constant';

const { mapGetters, mapActions, mapMutations } =
  createNamespacedHelpers('externalProjects');

export default {
  name: 'ExternalProjectsList',
  components: { InfiniteLoading, StatusBadge },
  data() {
    return {
      filters: [
        { label: 'All', value: null },
        { label: 'Pending', value: 'pending' },
        { label: 'Accepted', value: 'accepted' },
        { label: 'Rejected', value: 'rejected' },
      ],
      activeFilter: null,
      infiniteId: 0,
      fetchError: null,
    };
  },
  computed: {
    ...mapGetters(['projects', 'projectsNextToken']),
    kcBaseUrl() {
      return REMOTE_PATHS.API_PATH.includes('kingsch.at')
        ? 'https://kingschat.online'
        : 'https://stg.kingschat.online';
    },
  },
  beforeDestroy() {
    this.clearProjects();
    this.setProjectsStatusFilter(null);
  },
  methods: {
    ...mapActions(['fetchProjects']),
    ...mapMutations(['clearProjects', 'setProjectsStatusFilter']),
    async loadProjects(state) {
      this.fetchError = null;
      const { data, error } = await this.fetchProjects();
      if (error) {
        this.fetchError = this.getErrorMessage(error);
        state.complete();
        return;
      }
      if (data.data && data.data.length) {
        state.loaded();
      }
      if (!this.projectsNextToken) {
        state.complete();
      }
    },
    setFilter(status) {
      this.activeFilter = status;
      this.clearProjects();
      this.setProjectsStatusFilter(status);
      this.fetchError = null;
      this.infiniteId += 1;
    },
    retry() {
      this.fetchError = null;
      this.clearProjects();
      this.infiniteId += 1;
    },
    viewProject(id) {
      this.$router.push({ name: 'externalProjectDetail', params: { id } });
    },
    getErrorMessage(error) {
      const messages = {
        401: 'Your session has expired. Please log in again.',
        403: "You don't have permission to perform this action.",
        404: 'This item could not be found.',
        422: 'This action is no longer available for this item.',
        500: 'Something went wrong. Please try again.',
      };
      return (
        messages[error && error.status] ||
        'Something went wrong. Please try again.'
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.ExternalProjectsList {
  &__filters {
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
  }

  &__filter-btn {
    padding: 6px 16px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: white;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.15s;

    &:hover {
      border-color: #337ab7;
      color: #337ab7;
    }

    &--active {
      background: #337ab7;
      color: white;
      border-color: #337ab7;
    }
  }

  &__table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
      padding: 12px 16px;
      text-align: left;
      border-bottom: 1px solid #eee;
    }

    th {
      font-weight: 600;
      color: #666;
      font-size: 13px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
  }

  &__row {
    cursor: pointer;

    &:hover {
      background-color: #f8f9fa;
    }
  }

  &__error {
    padding: 14px 16px;
    background: #f8d7da;
    color: #721c24;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
  }

  &__owner-link {
    color: #337ab7;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__empty {
    text-align: center;
    padding: 32px;
    color: #999;
    font-size: 14px;
  }
}
</style>
